import { IconClose } from "@ui/Icons/IconClose";
import { IconMail } from "@ui/Icons/IconMail";
import type { NewsletterBarTranslations } from "@ui/Organisms/NewsletterBar/types";
import { type FormEvent, useEffect, useRef, useState } from "react";

export type NewsletterModalTranslations = NewsletterBarTranslations & {
	eyebrow: string;
};

type Props = {
	open: boolean;
	translations: NewsletterModalTranslations;
	logo?: string;
	onClose: () => void;
	onSubmit?: (email: string) => Promise<void>;
	/** Small trust line, e.g. ["4.200+ lezers", "Altijd uitschrijven"]. */
	socialProof?: string[];
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NewsletterModal({
	open,
	translations,
	logo,
	onClose,
	onSubmit,
	socialProof = [],
}: Props) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [email, setEmail] = useState("");
	const [error, setError] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);

	useEffect(() => {
		if (!open) return;
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		inputRef.current?.focus();
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => {
			document.body.style.overflow = previous;
			window.removeEventListener("keydown", onKey);
		};
	}, [open, onClose]);

	if (!open) return null;

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		if (!EMAIL_RE.test(email)) {
			setError(true);
			return;
		}
		setError(false);
		setSubmitting(true);
		try {
			await onSubmit?.(email);
			setDone(true);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[1200] flex items-end justify-center sm:items-center">
			<button
				type="button"
				aria-label={translations.closeLabel}
				onClick={onClose}
				className="absolute inset-0 cursor-default border-0 bg-[var(--overlay-backdrop)]"
			/>

			<section
				role="dialog"
				aria-modal="true"
				aria-labelledby="newsletter-modal-heading"
				className="relative z-10 flex w-full max-w-none flex-col gap-5 rounded-t-[var(--radius-lg)] bg-[var(--surface-default)] px-6 pb-7 pt-2.5 shadow-[var(--shadow-drawer)] sm:w-[520px] sm:max-w-[520px] sm:rounded-[var(--radius-lg)] sm:px-11 sm:pb-9 sm:pt-10 sm:shadow-[var(--shadow-popup)]"
			>
				<span className="mx-auto h-1 w-9 rounded-[var(--radius-pill)] bg-[var(--border-strong)] sm:hidden" />

				<button
					type="button"
					onClick={onClose}
					aria-label={translations.closeLabel}
					className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-[var(--radius-pill)] bg-[var(--surface-subtle)]"
				>
					<IconClose size={14} color="var(--content-muted)" />
				</button>

				{logo && (
					<img
						src={logo}
						alt=""
						className="h-14 w-14 rounded-[var(--radius-pill)] object-cover shadow-[var(--shadow-logo)]"
					/>
				)}

				{done ? (
					<div className="flex flex-col gap-2">
						<h2
							id="newsletter-modal-heading"
							className="m-0 text-[24px] font-extrabold tracking-[-0.02em] sm:text-[30px]"
							style={{ fontFamily: "var(--font-display)" }}
						>
							{translations.successHeading}
						</h2>
						<p className="m-0 text-[15px] leading-[1.6] text-[var(--content-muted)]">
							{translations.successSubtext}
						</p>
					</div>
				) : (
					<>
						<div className="flex flex-col gap-2">
							<span className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--accent-500)]">
								{translations.eyebrow}
							</span>
							<h2
								id="newsletter-modal-heading"
								className="m-0 text-[24px] font-extrabold leading-[1.18] tracking-[-0.02em] sm:text-[30px]"
								style={{ fontFamily: "var(--font-display)" }}
							>
								{translations.heading}
							</h2>
							<p className="m-0 text-[14px] leading-[1.6] text-[var(--content-muted)] sm:text-[15px]">
								{translations.subtext}
							</p>
						</div>

						<form
							onSubmit={submit}
							noValidate
							className="flex flex-col gap-2.5 sm:flex-row"
						>
							<label htmlFor="newsletter-modal-email" className="sr-only">
								{translations.placeholder}
							</label>
							<div
								className="flex flex-1 items-center gap-2.5 rounded-[var(--radius-sm)] border-[1.5px] px-4"
								style={{
									borderColor: error
										? "var(--accent-error)"
										: "var(--border-strong)",
								}}
							>
								<IconMail size={17} color="var(--content-faint)" />
								<input
									ref={inputRef}
									id="newsletter-modal-email"
									type="email"
									value={email}
									onChange={(e) => {
										setEmail(e.target.value);
										setError(false);
									}}
									placeholder={translations.placeholder}
									aria-invalid={error}
									className="h-[52px] flex-1 border-0 bg-transparent text-[15px] text-[var(--content-primary)] outline-none"
								/>
							</div>
							<button
								type="submit"
								disabled={submitting}
								className="h-[52px] rounded-[var(--radius-sm)] bg-[var(--surface-inverted)] px-6 text-[15px] font-bold text-[var(--content-inverted)] disabled:opacity-70"
							>
								{translations.submitLabel}
							</button>
						</form>

						{error && (
							<span className="text-[13px] font-medium text-[var(--accent-error)]">
								{translations.errorMessage}
							</span>
						)}

						{socialProof.length > 0 && (
							<div className="flex items-center gap-3.5 text-[12px] text-[var(--content-faint)]">
								{socialProof.map((item, i) => (
									<span key={item} className="flex items-center gap-3.5">
										{i > 0 && (
											<span className="h-[3px] w-[3px] rounded-full bg-[var(--border-strong)]" />
										)}
										{item}
									</span>
								))}
							</div>
						)}
					</>
				)}
			</section>
		</div>
	);
}
